import React, { useRef } from 'react'
import { Button } from '../../ui/button'
import { Field, Form, Formik, FormikHelpers } from 'formik'
import { TextInput } from '../TextInput'
import { SendHorizonal, UploadIcon } from 'lucide-react'
import { cn } from '../../../lib/utils'
import { TailwindModal } from '../../TailwindModal'
import { CheckIcon, SparklesIcon, TrashIcon } from '@heroicons/react/20/solid'
import { useProject } from '../../../model/hooks/useProject'
import { useToast } from '@chakra-ui/react'
import { DocumentCheckIcon } from '@heroicons/react/24/solid'
import { useAIChat } from '../../../model/hooks/useAIChat'
import { useAIFileUpload } from '../../../model/hooks/useAIFileUpload'
import {SmartDrawer} from "../../SmartDrawer";

interface Props {
  open: boolean
  setOpen: (open: boolean) => void
  onSubmit: (text: string) => void
  fieldHint: string
  aiShowAccReport?: boolean
}

export const AiHelpModal: React.FC<Props> = (props) => {
  const { id: projectId } = useProject()
  const scrollDiv = useRef<HTMLDivElement>(null)
  const toast = useToast()
  const { history, isLoading, message, sentMessage, sendMessage } = useAIChat({
    projectId,
    fieldHint: props.fieldHint,
    scrollRef: scrollDiv,
  })
  const { uploadedFiles, fileIsUploading, fileInputRef, handleUpload, removeFile } =
    useAIFileUpload()

  const onSubmit = async (values: any, formikHelpers: FormikHelpers<any>) => {
    formikHelpers.setSubmitting(true)
    formikHelpers.resetForm({ values: { query: '' } })

    await sendMessage(values.query, uploadedFiles)

    formikHelpers.setSubmitting(false)
  }

  return (
    <SmartDrawer
      title="Doplnit pomocí AI"
      open={props.open}
      setOpen={props.setOpen}
    >
      <div className="w-full mb-5">
        {(!!sentMessage || !!message || history.length > 0 || uploadedFiles.length > 0) && (
          <div
            ref={scrollDiv}
            className="w-fill h-[50vh] md:h-[60vh] overflow-scroll rounded-md p-3 mb-3 flex flex-col gap-3"
          >
            {uploadedFiles.length > 0 && (
              <div className="bg-gray-50 rounded-md p-3 shadow-md mb-3">
                <span className="text-left text-sm font-bold">Přiložené soubory:</span>
                <div className="flex flex-col items-end gap-3">
                  {uploadedFiles.map((file) => (
                    <span
                      key={file.file.uri}
                      className="flex gap-1 items-center bg-gray-100 text-gray-900 py-1 px-2 rounded-xl max-w-[80%] text-sm text-left"
                    >
                      <DocumentCheckIcon className="w-4 h-4 text-blue-600" />
                      {file.file.displayName?.slice(-30)}
                      <TrashIcon
                        className="w-4 h-4 text-red-600 cursor-pointer"
                        onClick={() => removeFile(file)}
                      />
                    </span>
                  ))}
                  <hr />
                </div>
              </div>
            )}
            {history.map((item, itemIndex) => (
              <div
                key={itemIndex}
                className={cn(
                  'text-left flex',
                  item.role === 'user' && 'justify-end',
                  item.role === 'model' && 'justify-start',
                )}
              >
                <div
                  className={cn(
                    'py-1 px-2 rounded-xl',
                    item.role === 'user' && 'bg-blue-600 text-white',
                    item.role === 'model' && 'text-gray-900',
                  )}
                >
                  {item.parts.map((p, pIndex) => (
                    <span key={pIndex}>{p.text}</span>
                  ))}
                  {item.role === 'model' && (
                    <div className="mt-3 flex justify-end">
                      <Button
                        variant="ghost"
                        onClick={() => {
                          props.onSubmit(item.parts[0]?.text! || '')
                        }}
                      >
                        <CheckIcon className="w-4 mr-1 text-blue-600" />
                        <span className="text-blue-600">Použít</span>
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {sentMessage && (
              <div className="flex justify-end">
                <span className="bg-blue-600 text-white py-1 px-2 rounded-xl max-w-[80%] text-left">
                  {sentMessage}
                </span>
              </div>
            )}
            {message && (
              <div className="flex justify-start">
                <span className=" text-gray-700 py-1 px-2 rounded-xl text-left">{message}</span>
              </div>
            )}
          </div>
        )}
        <div className="">
          <Formik initialValues={{ query: '' }} onSubmit={onSubmit}>
            {(formik) => (
              <Form>
                <div className="flex gap-3 w-full">
                  <input
                    ref={fileInputRef}
                    className="hidden"
                    type="file"
                    onChange={handleUpload}
                  />

                  <Button
                    isLoading={fileIsUploading}
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    size="sm"
                    variant="secondary"
                  >
                    <UploadIcon className="w-5" />
                  </Button>
                  <div className="flex-grow">
                    <Field name="query" component={TextInput} disabled={isLoading} />
                  </div>
                  <Button isLoading={isLoading || fileIsUploading} size="sm" type="submit">
                    <SendHorizonal className="w-5" />
                  </Button>
                </div>
                {props.aiShowAccReport &&
                  !message &&
                  !sentMessage &&
                  history.length === 0 &&
                  uploadedFiles.length === 0 && (
                    <div className="flex justify-center items-center gap-3 w-full mt-3">
                      <Button
                        type="button"
                        onClick={() => {
                          formik.setFieldValue('query', 'Vyplň na základě Průvodního listu.')
                          toast({
                            title: 'Předvyplněno',
                            status: 'success',
                          })
                        }}
                        variant="secondary"
                      >
                        <SparklesIcon className="h-5 w-5 mr-2 text-gray-600" />
                        "Vyplňit na základě Průvodního listu"
                      </Button>
                    </div>
                  )}
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </SmartDrawer>
  )
}
